/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import {
  Alert, Button, Grid, Skeleton,
} from '@mui/material';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { Sanitize } from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/Sanitizer';
import LoadingOverlay from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/LoadingOverlay';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  useSummaryForm,
} from './useSummaryForm';
import InputField from '../../../Components/FormFields/InputField';

/**
 * SummaryForm Component
 * @param props
 * @returns {Element}
 * @constructor
 */
export default function SummaryForm(props) {
  const { values } = useFormikContext();
  const { t } = useTranslation();

  const {
    fourthStepContent,
    voucherIsActivated,
    voucherIsRequired,
    referralCodeIsActivated,
    formField: { voucher, referralCode },
  } = props;

  const {
    isLoading, previewContract, validateVoucher, voucherErrorMessage, voucherSuccessMessage, voucherIsLoading,
  } = useSummaryForm(voucher.name);

  // While loading return the loading overlay
  if (isLoading) {
    return (
      <>
        <LoadingOverlay />
        <Skeleton variant="rectangular" width={600} height={400} />
      </>
    );
  }

  const preventFormSubmitByEnterKey = (event, callbackFn) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (callbackFn) callbackFn();
    }
  };

  /**
     * Render flat fees
     */
  const renderFlatFees = () => previewContract.flatFeePreviews.map((feeData) => (
    <Grid container spacing={2} key={feeData.identifier}>
      <Grid item xs={6}>
        {feeData.name}
        :
      </Grid>
      <Grid item xs={6}>
        {feeData.paymentFrequency}
        {' '}
        {feeData.discountedPrice !== null ? feeData.discountedPrice : feeData.price}
        €
      </Grid>
    </Grid>
  ));

  const genderMap = {
    MALE: 'männlich',
    FEMALE: 'weiblich',
  };
  const genderLabel = genderMap[values.gender] || 'divers';

  return (
    <>
      <Sanitize value={fourthStepContent} />
      {voucherIsLoading ? <LoadingOverlay /> : null}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          {t('firstname')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.firstName}
        </Grid>
        <Grid item xs={6}>
          {t('surname')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.lastName}
        </Grid>
        <Grid item xs={6}>
          {t('gender')}
          :
        </Grid>
        <Grid item xs={6}>
          {genderLabel}
        </Grid>
        <Grid item xs={6}>
          {t('streetAndHouseNumber')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.street}
          {' '}
          {values.houseNumber}
        </Grid>
        <Grid item xs={6}>
          {t('plz')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.postCode}
        </Grid>
        <Grid item xs={6}>
          {t('city')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.city}
        </Grid>
        <Grid item xs={6}>
          {t('mail')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.email}
        </Grid>
        <Grid item xs={6}>
          {t('phone')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.phonePrefix}
          {values.phone}
        </Grid>
        <Grid item xs={6}>
          {t('birthday')}
          :
        </Grid>
        <Grid item xs={6}>
          {dayjs(values.dateOfBirth).format('DD.MM.YYYY')}
        </Grid>
        <hr />
        <Grid item xs={12}>
          <strong>
            {t('bank')}
            :
          </strong>
        </Grid>

        <Grid item xs={6}>
          {t('iban')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.iban}
        </Grid>

        <Grid item xs={12}>
          <hr />
        </Grid>
        {voucherIsActivated ? (
          <>
            <Grid item xs={12}>
              <strong>
                {t('voucher')}
                :
              </strong>
            </Grid>
            <Grid item xs={8}>
              <InputField
                name={voucher.name}
                label={voucher.label}
                fullWidth
                error={!!voucherErrorMessage}
                onKeyPress={(event) => preventFormSubmitByEnterKey(event, validateVoucher)}
                required={voucherIsRequired}
              />
            </Grid>
            <Grid item xs={4}>
              <Button fullWidth variant="contained" color="primary" onClick={validateVoucher}>
                {voucher.button}
              </Button>
            </Grid>
            <Grid item xs={12}>
              {voucherSuccessMessage ? <Alert severity="success">{voucherSuccessMessage}</Alert> : null}
              {voucherErrorMessage ? <Alert severity="error">{voucherErrorMessage}</Alert> : null}
            </Grid>
          </>
        ) : null}

        <Grid item xs={12}>
          <strong>
            {t('studioAndCosts')}
            :
          </strong>
        </Grid>

        <Grid item xs={6}>
          {t('selection')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.studioName}
        </Grid>
        <Grid item xs={6}>
          {t('contract')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.contractName}
        </Grid>
        <Grid item xs={6}>
          {t('monthlyCost')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.contractTerm.price}
          {t('currency')}
        </Grid>
        {values.voucherCode ? (
          <>
            <Grid item xs={6}>
              {t('code')}
              :
            </Grid>
            <Grid item xs={6}>
              {values.voucherCode}
            </Grid>
          </>
        ) : null}
        <hr />
        {previewContract ? (
          <>
            <Grid item xs={12}>
              <strong>
                {t('costs')}
                :
              </strong>
            </Grid>
            <Grid item xs={12}>
              {renderFlatFees()}
            </Grid>
            <hr />
          </>
        ) : null}
        {referralCodeIsActivated ? (
          <>
            <Grid item xs={12}>
              <hr />
            </Grid>
            <Grid item xs={12}>
              <strong>{t('applyReferralCode')}</strong>
            </Grid>
            <Grid item xs={12}>
              <InputField
                name={referralCode.name}
                label={referralCode.label}
                fullWidth
                onKeyPress={preventFormSubmitByEnterKey}
              />
            </Grid>
          </>
        ) : null}
      </Grid>
    </>
  );
}

SummaryForm.propTypes = {
  fourthStepContent: PropTypes.object.isRequired,
  voucherIsActivated: PropTypes.object,
  voucherIsRequired: PropTypes.object,
  referralCodeIsActivated: PropTypes.bool.isRequired,
  formField: PropTypes.object.isRequired,
};

SummaryForm.defaultProps = {
  voucherIsActivated: null,
  voucherIsRequired: null,
};
